import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const SystemStatusContext = createContext();

export const useSystemStatus = () => useContext(SystemStatusContext);

export const SystemStatusProvider = ({ children }) => {
    const [statusInfo, setStatusInfo] = useState({ status: 'normal', message: '' });
    const [maintenanceConfig, setMaintenanceConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const statusDocRef = doc(db, 'system_status', 'current');
        const maintDocRef = doc(db, 'systemSettings', 'maintenanceConfig');

        const unsubscribeStatus = onSnapshot(statusDocRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                setStatusInfo({ status: data.status, message: data.message });
            } else {
                setStatusInfo({ status: 'normal', message: '' });
            }
        }, (error) => {
            console.error("Error fetching system status:", error);
        });

        const unsubscribeMaint = onSnapshot(maintDocRef, (doc) => {
            if (doc.exists()) {
                setMaintenanceConfig(doc.data());
            } else {
                setMaintenanceConfig(null);
            }
            setLoading(false);
        }, (error) => {
            console.error("Error fetching maintenance config:", error);
            setLoading(false);
        });

        return () => {
            unsubscribeStatus();
            unsubscribeMaint();
        };
    }, []);

    const isMaintenanceActive = useCallback(() => {
        if (!maintenanceConfig || !maintenanceConfig.enabled) return false;
        const now = new Date();
        const startAt = maintenanceConfig.startAt?.toDate();
        const endAt = maintenanceConfig.endAt?.toDate();
        return startAt && endAt && now >= startAt && now < endAt;
    }, [maintenanceConfig]);

    const checkMaintenance = useCallback(() => {
        return isMaintenanceActive();
    }, [isMaintenanceActive]);

    const value = {
        status: statusInfo.status,
        message: statusInfo.message,
        maintenanceConfig,
        isMaintenanceActive: isMaintenanceActive(),
        checkMaintenance,
        isLoading: loading,
    };

    return (
        <SystemStatusContext.Provider value={value}>
            {children}
        </SystemStatusContext.Provider>
    );
};
